import React from 'react';
import { Personnel } from '../../../../shared/types/game';
import { getPlanningServiceKeyFromBuildingType } from '../../utils/buildingServiceMapping';
import {
  resolvePlanningRowForLabel,
  planningBadgeCode,
} from './buildingDetailsTabHelpers';

interface BuildingPersonnelPlanningSectionProps {
  personnel: Personnel[];
  personnelsDisponibles: number;
  personnelsDispoDepart: number;
  personnelParPlanning: ReadonlyArray<readonly [string, Personnel[]]>;
  planningServiceKey: ReturnType<typeof getPlanningServiceKeyFromBuildingType>;
}

/** Section « Personnels » groupés par statut planning du jour, extraite de BuildingDetailsTab. */
const BuildingPersonnelPlanningSection: React.FC<BuildingPersonnelPlanningSectionProps> = ({
  personnel,
  personnelsDisponibles,
  personnelsDispoDepart,
  personnelParPlanning,
  planningServiceKey,
}) => (
  <section className="rounded-xl border border-zinc-700/80 bg-zinc-900/70 p-5 shadow-lg shadow-black/25">
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-lg border border-violet-500/35 bg-violet-500/15 text-violet-300"
          aria-hidden
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        </span>
        <h2 className="text-base font-extrabold uppercase tracking-wide text-emerald-300/95">
          Personnels
        </h2>
      </div>
      <p className="text-sm font-medium text-zinc-400">
        Total : {personnel.length}
        <span className="text-zinc-600"> • </span>
        Disponibles : {personnelsDisponibles}
        <span className="text-zinc-600"> • </span>
        Dispo départ : {personnelsDispoDepart}
      </p>
    </div>

    {personnel.length === 0 ? (
      <p className="py-8 text-center text-sm text-zinc-500">Aucun personnel assigné.</p>
    ) : (
      <div className="space-y-[1.35rem]">
        {personnelParPlanning.map(([statutLabel, rows]) => {
          const planRow = resolvePlanningRowForLabel(statutLabel, planningServiceKey);
          const badgeStyle: React.CSSProperties = planRow
            ? {
                backgroundColor: planRow.couleurs.backgroundColor,
                color: planRow.couleurs.textColor,
              }
            : {
                backgroundColor: '#3f3f46',
                color: '#fafafa',
              };

          return (
            <article
              key={statutLabel}
              className="overflow-hidden rounded-lg border border-zinc-700/90 bg-zinc-950/50 shadow-md shadow-black/20"
            >
              <div className="flex flex-col gap-2 border-b border-zinc-800 bg-gradient-to-b from-zinc-800/90 to-zinc-900/90 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:py-2">
                <span className="text-[0.72rem] font-bold uppercase leading-snug tracking-[0.06em] text-zinc-200">
                  {statutLabel}
                  <span className="ml-2 font-extrabold text-zinc-500">· {rows.length}</span>
                </span>
                <span
                  className="inline-flex w-fit shrink-0 rounded-full border border-zinc-600/50 px-2.5 py-0.5 text-[0.68rem] font-semibold tracking-wide"
                  style={badgeStyle}
                >
                  {planningBadgeCode(planRow, statutLabel)}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                {rows.map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg border border-emerald-500/20 bg-gradient-to-b from-emerald-600 to-emerald-800 px-4 py-2.5 shadow-md shadow-black/25 transition-[transform,box-shadow] hover:-translate-y-px hover:shadow-lg"
                  >
                    <div className="text-[0.82rem] font-bold uppercase leading-snug tracking-wide text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.25)]">
                      {p.prenom} {p.nom}
                    </div>
                    <div className="mt-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-emerald-100/90">
                      {p.grade}
                    </div>
                  </div>
                ))}
              </div>
            </article>
          );
        })}
      </div>
    )}
  </section>
);

export default BuildingPersonnelPlanningSection;
